import { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Upload, Download, Check, Loader2, AlertTriangle, RefreshCw, Users, Trash2, X } from 'lucide-react';
import useStore from '../store/useStore';
import apiClient from '../services/api';

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL;
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM;
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

function RoleCard({ role, label, description, icon: Icon, enabled, onChange }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
        enabled
          ? 'border-emerald-500 bg-emerald-900/20'
          : 'border-gray-700 bg-gray-800/30 hover:border-gray-600'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            enabled ? 'bg-emerald-600' : 'bg-gray-700'
          }`}>
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <p className={`font-semibold ${enabled ? 'text-emerald-400' : 'text-gray-300'}`}>{label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
          enabled ? 'border-emerald-500 bg-emerald-500' : 'border-gray-600'
        }`}>
          {enabled && <Check size={12} className="text-white" />}
        </div>
      </div>
    </button>
  );
}

export default function Settings() {
  const { user, setUser, addNotification } = useStore();
  const [isProvider, setIsProvider] = useState(
    user?.roles?.includes('data-provider') || user?.roles?.includes('admin') || user?.roles?.includes('realm-admin') || false
  );
  const [isConsumer, setIsConsumer] = useState(
    user?.roles?.includes('data-consumer') || user?.roles?.includes('admin') || user?.roles?.includes('realm-admin') || false
  );

  const isAdmin = user?.roles?.includes('admin') || user?.roles?.includes('realm-admin');

  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [confirmingUsername, setConfirmingUsername] = useState(null);
  const [deletingUsername, setDeletingUsername] = useState(null);

  const loadUsers = async () => {
    setLoadingUsers(true);
    setUsersError('');
    try {
      const result = await apiClient.get('/auth/users');
      setUsers(result.data || []);
    } catch (err) {
      setUsersError(err.response?.data?.error || err.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  const handleDeleteUser = async (username) => {
    setDeletingUsername(username);
    try {
      await apiClient.delete(`/auth/users/${encodeURIComponent(username)}`);
      setUsers(prev => prev.filter(u => u.username !== username));
      addNotification({ type: 'success', message: `User "${username}" and their data were deleted` });
    } catch (err) {
      addNotification({ type: 'error', message: err.response?.data?.error || `Failed to delete "${username}"` });
    } finally {
      setDeletingUsername(null);
      setConfirmingUsername(null);
    }
  };

  const hasChanges = () => {
    const currentProvider = user?.roles?.includes('data-provider') ?? false;
    const currentConsumer = user?.roles?.includes('data-consumer') ?? false;
    return isProvider !== currentProvider || isConsumer !== currentConsumer;
  };

  const handleSave = async () => {
    if (!isProvider && !isConsumer) {
      setErrorMsg('You must keep at least one role.');
      setStatus('error');
      return;
    }

    setSaving(true);
    setStatus(null);
    setErrorMsg('');

    try {
      const newRoles = [
        ...(isProvider ? ['data-provider'] : []),
        ...(isConsumer ? ['data-consumer'] : []),
      ];

      const result = await apiClient.patch('/auth/user/roles', { roles: newRoles });
      const confirmedRoles = result.data?.roles || newRoles;

      // Try to refresh the Keycloak token to get an updated JWT with new roles
      let tokenRefreshed = false;
      const refreshToken = localStorage.getItem('kc_refresh_token');
      if (refreshToken && KEYCLOAK_URL && KEYCLOAK_REALM && KEYCLOAK_CLIENT_ID) {
        try {
          const tokenRes = await fetch(
            `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
              body: new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: KEYCLOAK_CLIENT_ID,
                refresh_token: refreshToken,
              }),
            }
          );

          if (tokenRes.ok) {
            const tokenData = await tokenRes.json();
            localStorage.setItem('kc_token', tokenData.access_token);
            if (tokenData.refresh_token) localStorage.setItem('kc_refresh_token', tokenData.refresh_token);

            const payload = JSON.parse(atob(tokenData.access_token.split('.')[1]));
            const realmRoles = payload.realm_access?.roles || [];
            const clientRoles = payload.resource_access?.['realm-management']?.roles || [];
            const updatedRoles = [...new Set([...realmRoles, ...clientRoles])];

            localStorage.setItem('userRoles', JSON.stringify(updatedRoles));
            setUser({ username: user.username, email: user.email, roles: updatedRoles });
            tokenRefreshed = true;
          }
        } catch (_) {
          // token refresh failed — fall through to manual update
        }
      }

      if (!tokenRefreshed) {
        // Token refresh unavailable — update store directly from confirmed backend roles
        const otherRoles = (user?.roles || []).filter(r => !['data-provider', 'data-consumer'].includes(r));
        const updatedRoles = [...otherRoles, ...confirmedRoles];
        localStorage.setItem('userRoles', JSON.stringify(updatedRoles));
        setUser({ username: user.username, email: user.email, roles: updatedRoles });
      }

      addNotification({ type: 'success', message: `Roles updated: ${confirmedRoles.map(r => r.replace('data-', '')).join(', ')}` });
      setStatus('success');
    } catch (err) {
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to update roles');
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h1 className="text-3xl font-bold font-display text-white mb-2">Settings</h1>
        <p className="text-gray-400">Manage your account preferences</p>
      </div>

      {/* Profile info */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-800">
            <span className="text-sm text-gray-400">Username</span>
            <span className="text-sm text-white font-medium">{user?.username || '—'}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-400">Current roles</span>
            <div className="flex gap-1.5">
              {user?.roles?.filter(r => ['data-provider', 'data-consumer', 'admin', 'realm-admin'].includes(r)).map(r => (
                <span key={r} className="text-xs px-2 py-0.5 bg-blue-600/30 text-blue-400 rounded capitalize">
                  {r === 'realm-admin' ? 'admin' : r.replace('data-', '')}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Role management */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-1">Connector Roles</h2>
        <p className="text-sm text-gray-400 mb-4">
          Choose whether you act as a data provider, consumer, or both in this dataspace.
        </p>

        {isAdmin ? (
          <div className="flex items-center gap-2 p-3 bg-blue-900/20 border border-blue-800 rounded-lg text-sm text-blue-300 mb-4">
            <SettingsIcon size={15} />
            You have admin privileges — your access is not restricted by provider/consumer roles.
          </div>
        ) : (
          <div className="space-y-3 mb-4">
            <RoleCard
              role="data-provider"
              label="Provider"
              description="Publish assets, create policies and data offers"
              icon={Upload}
              enabled={isProvider}
              onChange={setIsProvider}
            />
            <RoleCard
              role="data-consumer"
              label="Consumer"
              description="Browse catalogs, negotiate contracts and transfer data"
              icon={Download}
              enabled={isConsumer}
              onChange={setIsConsumer}
            />
          </div>
        )}

        {status === 'error' && (
          <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg text-sm text-red-300 mb-3">
            <AlertTriangle size={15} />
            {errorMsg}
          </div>
        )}

        {status === 'success' && (
          <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-800 rounded-lg text-sm text-green-300 mb-3">
            <Check size={15} />
            Roles updated. Your session has been refreshed.
          </div>
        )}

        {!isAdmin && (
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges()}
            className={`btn flex items-center gap-2 ${
              hasChanges() && !saving ? 'btn-primary' : 'btn-secondary opacity-50 cursor-not-allowed'
            }`}
          >
            {saving ? <><Loader2 size={16} className="animate-spin" />Saving...</> : <><RefreshCw size={16} />Apply Changes</>}
          </button>
        )}
      </div>

      {isAdmin && (
        <div className="card">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users size={18} />
              User Management
            </h2>
            <button
              onClick={loadUsers}
              disabled={loadingUsers}
              className="text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={16} className={loadingUsers ? 'animate-spin' : ''} />
            </button>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Deleting a user removes their Keycloak account and all of their app data (connector settings, notifications, automation jobs, transfers). This cannot be undone.
          </p>

          {usersError && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800 rounded-lg text-sm text-red-300 mb-3">
              <AlertTriangle size={15} />
              {usersError}
            </div>
          )}

          {loadingUsers ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
              <Loader2 size={16} className="animate-spin" />
              Loading users...
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {users.map(u => (
                <div
                  key={u.id}
                  className="flex items-center justify-between py-2 px-3 rounded-lg border border-gray-800 bg-gray-800/30"
                >
                  <div>
                    <p className="text-sm text-white font-medium">{u.username}</p>
                    <p className="text-xs text-gray-500">{u.email || '—'}</p>
                  </div>

                  {confirmingUsername === u.username ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-red-400">Delete permanently?</span>
                      <button
                        onClick={() => handleDeleteUser(u.username)}
                        disabled={deletingUsername === u.username}
                        className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-1"
                      >
                        {deletingUsername === u.username ? <Loader2 size={12} className="animate-spin" /> : null}
                        Confirm
                      </button>
                      <button
                        onClick={() => setConfirmingUsername(null)}
                        disabled={deletingUsername === u.username}
                        className="text-xs px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmingUsername(u.username)}
                      className="text-gray-500 hover:text-red-400 transition-colors"
                      title={`Delete ${u.username}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              {users.length === 0 && (
                <p className="text-sm text-gray-500 py-2">No users found.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
